/**
 * prepareNetwork - Convenience function that auto-detects and applies transformations
 *
 * Intelligently prepares network data for ForceGraph by:
 * 1. Creating nodes from links if nodes array is missing
 * 2. Applying property mappings
 * 3. Computing groups if requested
 *
 * @param {Object|Array} data - Network data or links-only array
 * @param {Object} options - Transformation options
 * @param {Object} [options.nodeMapping] - Property mappings for nodes { to: from }
 * @param {Object} [options.linkMapping] - Property mappings for links { to: from }
 * @param {string|function} [options.groupMethod] - Method for computing groups ('connected', 'degree', or function)
 * @param {string} [options.groupProperty='group'] - Property name for computed groups
 * @param {function} [options.nodeId] - Node ID accessor (default: d => d.id)
 * @param {function} [options.nodeProps] - Create node properties from ID (for edge-only data)
 * @param {function} [options.linkSource] - Extract source from link (default: d => d.source)
 * @param {function} [options.linkTarget] - Extract target from link (default: d => d.target)
 * @returns {Object} { nodes, links } ready for ForceGraph
 *
 * @example
 * // Auto-detect and prepare edge-only data
 * const data = [{ source: 0, target: 1 }, { source: 1, target: 2 }];
 * const graph = prepareNetwork(data);
 *
 * @example
 * // Prepare with property mappings
 * const graph = prepareNetwork(rawData, {
 *   nodeMapping: { id: 'name', group: 'country' },
 *   groupMethod: 'connected'
 * });
 *
 * @example
 * // Full transformation pipeline
 * const graph = prepareNetwork(edgeData, {
 *   nodeProps: (id) => ({ id, label: `Node ${id}` }),
 *   groupMethod: 'degree',
 *   groupProperty: 'cluster'
 * });
 */
import { nodesFromLinks } from "./nodesFromLinks.js";
import { mapProperties } from "./mapProperties.js";
import { computeNodeGroups } from "./computeNodeGroups.js";

export function prepareNetwork(
  data,
  {
    nodeMapping,
    linkMapping,
    groupMethod,
    groupProperty = "group",
    nodeId = (d) => d.id,
    nodeProps,
    linkSource = (d) => d.source,
    linkTarget = (d) => d.target,
  } = {}
) {
  let network;

  // Step 1: Handle edge-only data (array input or missing nodes)
  if (Array.isArray(data)) {
    // Data is just an array of links
    network = nodesFromLinks(data, {
      linkSource,
      linkTarget,
      nodeProps: nodeProps || ((id) => ({ id })),
    });
  } else if (!data.nodes || data.nodes.length === 0) {
    // Data has links property but no nodes
    network = nodesFromLinks(data.links || [], {
      linkSource,
      linkTarget,
      nodeProps: nodeProps || ((id) => ({ id })),
    });
  } else {
    // Data already has nodes and links
    network = {
      nodes: [...data.nodes],
      links: [...(data.links || [])],
    };
  }

  // Step 2: Apply property mappings if provided
  if (nodeMapping || linkMapping) {
    network = mapProperties(network, {
      nodes: nodeMapping,
      links: linkMapping,
    });
  }

  // Step 3: Compute groups if requested
  if (groupMethod) {
    network = computeNodeGroups(network, {
      method: groupMethod,
      groupProperty,
      nodeId,
      linkSource,
      linkTarget,
    });
  }

  return network;
}
